'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import {
  ChevronDown,
  Mail,
  Menu,
  MessageCircle,
  Phone,
  X,
} from 'lucide-react'
import SafeImage from './SafeImage'
import { SITE } from '@/lib/site'
import { products } from '@/data/homepage'

const navLinks = [
  { label: 'Anasayfa', href: '/' },
  { label: 'Hakkımızda', href: '/hakkimizda/' },
  {
    label: 'Akustik Paneller',
    href: '/akustik-paneller/',
    children: products.map((p) => ({ label: p.name, href: p.href })),
  },
  { label: 'Akustik Panel', href: '/akustik-panel/' },
  { label: 'Akustik Panel Fiyatları', href: '/akustik-panel-fiyatlari/' },
  { label: 'İletişim', href: '/iletisim/' },
]

export default function Header() {
  const [open, setOpen] = useState(false)
  const [dropdown, setDropdown] = useState(false)
  const [productsOpen, setProductsOpen] = useState(true)
  const mobileMenuRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    const menu = mobileMenuRef.current
    if (!menu) return
    if (open) menu.removeAttribute('inert')
    else menu.setAttribute('inert', '')
  }, [open])

  const closeMenu = () => setOpen(false)

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-[100] text-white shadow-md"
        style={{ background: 'linear-gradient(-45deg, #01124d 0%, #014b64 100%)' }}
      >
        <div className="w-full px-4 lg:px-5 xl:px-6">
          <div className="flex items-center gap-3 xl:gap-4 h-16 md:h-[72px]">
            <Link href="/" className="flex items-center shrink-0" aria-label={SITE.name}>
              <SafeImage
                src="/images/header-footer-logo.webp"
                alt={SITE.name}
                width={100}
                height={48}
                className="h-9 w-auto md:h-10 object-contain"
                priority
              />
            </Link>

            <nav className="hidden lg:flex flex-1 items-center justify-between min-w-0 pr-8 xl:pr-14 2xl:pr-20">
              {navLinks.map((link) =>
                link.children ? (
                  <div
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => setDropdown(true)}
                    onMouseLeave={() => setDropdown(false)}
                  >
                    <Link
                      href={link.href}
                      className="inline-flex items-center gap-1 px-0.5 py-2 text-sm xl:text-[15px] font-medium whitespace-nowrap hover:text-[#f7941d]"
                    >
                      {link.label}
                      <ChevronDown size={15} className="shrink-0" />
                    </Link>
                    {dropdown && (
                      <div className="absolute top-full left-0 min-w-[240px] bg-white text-[#232f3e] shadow-xl border-t-2 border-[#f7941d] rounded-b-md py-2 z-50">
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            className="block px-4 py-2 text-sm hover:bg-gray-50 hover:text-[#f7941d]"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="px-0.5 py-2 text-sm xl:text-[15px] font-medium whitespace-nowrap hover:text-[#f7941d]"
                  >
                    {link.label}
                  </Link>
                )
              )}

              <a
                href={`tel:${SITE.phoneTel}`}
                className="inline-flex items-center gap-1.5 px-0.5 py-2 text-sm xl:text-[15px] font-semibold whitespace-nowrap text-[#feb400] hover:text-[#f7941d]"
              >
                <Phone size={15} className="shrink-0" />
                <span className="whitespace-nowrap">{SITE.phoneDisplay}</span>
              </a>
            </nav>

            <div className="flex items-center gap-2 shrink-0 ml-auto lg:ml-0">
              <a
                href={SITE.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex items-center gap-2 bg-[#0f766e] hover:bg-[#0d9488] text-white px-3.5 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap min-h-[44px]"
              >
                <MessageCircle size={16} className="shrink-0" />
                Whatsapp İle Ulaşın
              </a>
              <button
                type="button"
                aria-label={open ? 'Menüyü kapat' : 'Menüyü aç'}
                aria-expanded={open}
                className="lg:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-[#f7941d]"
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[110] bg-black/45 transition-opacity duration-300 lg:hidden ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMenu}
        aria-hidden={!open}
      />

      {/* Full-width slide-in from right */}
      <aside
        ref={mobileMenuRef}
        className={`fixed inset-y-0 right-0 z-[120] w-full bg-white text-[#111827] shadow-2xl transition-transform duration-300 ease-out lg:hidden flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
        aria-label="Mobil menü"
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100 shrink-0">
          <Link href="/" onClick={closeMenu} className="flex items-center" aria-label={SITE.name}>
            <SafeImage
              src="/images/header-footer-logo.webp"
              alt={SITE.name}
              width={110}
              height={48}
              className="h-9 w-auto object-contain brightness-0"
            />
          </Link>
          <button
            type="button"
            aria-label="Menüyü kapat"
            onClick={closeMenu}
            className="w-11 h-11 rounded-full bg-gray-100 text-[#111827] flex items-center justify-center hover:bg-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <nav className="px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.href} className="pb-1">
                  <div className="flex items-center gap-1">
                    <Link
                      href={link.href}
                      onClick={closeMenu}
                      className="flex-1 py-3 px-3 rounded-lg text-[15px] font-semibold text-[#111827] hover:bg-gray-50"
                    >
                      {link.label}
                    </Link>
                    <button
                      type="button"
                      aria-label="Ürünleri aç/kapat"
                      aria-expanded={productsOpen}
                      onClick={() => setProductsOpen((v) => !v)}
                      className="w-11 h-11 rounded-lg flex items-center justify-center text-[#4b5563] hover:bg-gray-50"
                    >
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-200 ${
                          productsOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </div>
                  {productsOpen && (
                    <div className="ml-2 mt-1 mb-2 border-l-2 border-[#f7941d]/40 pl-3 space-y-0.5">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          href={child.href}
                          onClick={closeMenu}
                          className="block py-2.5 px-3 rounded-md text-sm text-[#374151] hover:bg-orange-50 hover:text-[#b45309]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={closeMenu}
                  className="block py-3 px-3 rounded-lg text-[15px] font-semibold text-[#111827] hover:bg-gray-50"
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="mx-4 mb-6 rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white p-4 space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6b7280]">
              İletişim
            </p>

            <a
              href={`tel:${SITE.phoneTel}`}
              className="flex items-center gap-3 min-h-[44px] rounded-xl bg-white border border-gray-100 px-3 py-2.5 hover:border-[#f7941d]/50"
            >
              <span className="w-9 h-9 rounded-full bg-[#fff7ed] text-[#b45309] flex items-center justify-center shrink-0">
                <Phone size={16} />
              </span>
              <span>
                <span className="block text-[11px] text-[#6b7280]">Çağrı merkezi</span>
                <span className="block text-sm font-semibold text-[#111827]">
                  {SITE.phoneDisplay}
                </span>
              </span>
            </a>

            <a
              href={`tel:${SITE.mobileTel}`}
              className="flex items-center gap-3 min-h-[44px] rounded-xl bg-white border border-gray-100 px-3 py-2.5 hover:border-[#f7941d]/50"
            >
              <span className="w-9 h-9 rounded-full bg-[#fff7ed] text-[#b45309] flex items-center justify-center shrink-0">
                <Phone size={16} />
              </span>
              <span>
                <span className="block text-[11px] text-[#6b7280]">Mobil</span>
                <span className="block text-sm font-semibold text-[#111827]">
                  {SITE.mobileDisplay}
                </span>
              </span>
            </a>

            <a
              href={`tel:${SITE.officePhoneTel}`}
              className="flex items-center gap-3 min-h-[44px] rounded-xl bg-white border border-gray-100 px-3 py-2.5 hover:border-[#f7941d]/50"
            >
              <span className="w-9 h-9 rounded-full bg-[#fff7ed] text-[#b45309] flex items-center justify-center shrink-0">
                <Phone size={16} />
              </span>
              <span>
                <span className="block text-[11px] text-[#6b7280]">Ofis</span>
                <span className="block text-sm font-semibold text-[#111827]">
                  {SITE.officePhoneDisplay}
                </span>
              </span>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              className="flex items-center gap-3 min-h-[44px] rounded-xl bg-white border border-gray-100 px-3 py-2.5 hover:border-[#f7941d]/50"
            >
              <span className="w-9 h-9 rounded-full bg-[#eff6ff] text-[#014b64] flex items-center justify-center shrink-0">
                <Mail size={16} />
              </span>
              <span>
                <span className="block text-[11px] text-[#6b7280]">E-posta</span>
                <span className="block text-sm font-semibold text-[#111827] break-all">
                  {SITE.email}
                </span>
              </span>
            </a>

            <a
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex items-center justify-center gap-2 w-full min-h-[48px] rounded-xl bg-[#0f766e] hover:bg-[#0d9488] text-white text-sm font-semibold"
            >
              <MessageCircle size={18} />
              WhatsApp ile Ulaşın
            </a>
          </div>
        </div>
      </aside>

      <div className="h-16 md:h-[72px]" aria-hidden />
    </>
  )
}
